"use client";

import { useMemo, useState } from "react";
import {
  AlertTriangle,
  Code2,
  Eye,
  EyeOff,
  FileWarning,
  Play,
  Rows3,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ClearButton } from "@/components/tools/clear-button";
import { CopyButton } from "@/components/tools/copy-button";
import { ErrorMessage } from "@/components/tools/error-message";
import { InputOutputPanel } from "@/components/tools/input-output-panel";
import { CodeOutput } from "@/components/tools/tool-output";
import {
  displayValue,
  formatCurlBodyPreview,
  formatCurlReport,
  generateCurlSnippets,
  translateCurlCommand,
  type CurlBodyField,
  type CurlHeader,
  type CurlQueryParam,
  type CurlSnippetKey,
  type CurlTranslation,
  type CurlWarning,
} from "@/lib/tools/curl";
import { cn } from "@/lib/utils";

const curlExample = `curl 'https://api.example.com/v1/customers?active=true' \\
  -X POST \\
  -H 'Authorization: Bearer sk_test_1234567890' \\
  -H 'Content-Type: application/json' \\
  --data-raw '{"name":"Ada Lovelace","email":"ada@example.com","plan":"pro"}'`;

const snippetTabs: Array<{ id: CurlSnippetKey; label: string }> = [
  { id: "fetch", label: "Fetch" },
  { id: "axios", label: "Axios" },
  { id: "python", label: "Python" },
  { id: "httpie", label: "HTTPie" },
  { id: "curl", label: "cURL" },
];

export function CurlTranslatorTool() {
  const [input, setInput] = useState("");
  const [translation, setTranslation] = useState<CurlTranslation | null>(null);
  const [error, setError] = useState("");
  const [activeSnippet, setActiveSnippet] = useState<CurlSnippetKey>("fetch");
  const [redactSecrets, setRedactSecrets] = useState(true);

  const snippets = useMemo(
    () => (translation ? generateCurlSnippets(translation, { redactSecrets }) : null),
    [translation, redactSecrets],
  );
  const report = useMemo(
    () => (translation ? formatCurlReport(translation, redactSecrets) : ""),
    [translation, redactSecrets],
  );

  const process = () => {
    const result = translateCurlCommand(input);
    if (result.ok) {
      setTranslation(result.value);
      setError("");
      return;
    }

    setTranslation(null);
    setError(result.error);
  };

  return (
    <div className="space-y-4">
      <InputOutputPanel
        inputTitle="cURL command"
        outputTitle="Explanation"
        inputActions={
          <>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => {
                setInput(curlExample);
                setTranslation(null);
                setError("");
              }}
            >
              <Sparkles className="h-4 w-4" />
              Example
            </Button>
            <ClearButton
              onClear={() => {
                setInput("");
                setTranslation(null);
                setError("");
              }}
            />
          </>
        }
        outputActions={<CopyButton value={report} label="Copy report" />}
        input={
          <div className="space-y-3">
            <TextareaLike
              value={input}
              onChange={(value) => {
                setInput(value);
                setError("");
              }}
            />
            <div className="flex flex-wrap items-center justify-between gap-3">
              <label className="flex items-center gap-2 text-sm text-zinc-700">
                <input
                  type="checkbox"
                  checked={redactSecrets}
                  onChange={(event) => setRedactSecrets(event.target.checked)}
                />
                <span>Redact secrets</span>
              </label>
              <Button type="button" onClick={process}>
                <Play className="h-4 w-4" />
                Translate cURL
              </Button>
            </div>
            <ErrorMessage message={error} />
          </div>
        }
        output={
          translation ? (
            <ExplanationPanel translation={translation} redactSecrets={redactSecrets} />
          ) : (
            <CodeOutput placeholder="The translated request summary will appear here." />
          )
        }
      />

      {translation && snippets ? (
        <>
          <section className="rounded-lg border border-zinc-200 bg-white">
            <div className="flex min-h-12 flex-col gap-3 border-b border-zinc-200 px-4 py-3 md:flex-row md:items-center md:justify-between">
              <div className="flex items-center gap-2">
                <Code2 className="h-4 w-4 text-zinc-500" />
                <h2 className="text-sm font-semibold text-zinc-950">Generated code</h2>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <SnippetTabs activeSnippet={activeSnippet} onChange={setActiveSnippet} />
                <CopyButton value={snippets[activeSnippet]} label="Copy code" />
              </div>
            </div>
            <div className="p-4">
              <CodeOutput value={snippets[activeSnippet]} className="min-h-72" />
            </div>
          </section>

          <RequestDetails translation={translation} redactSecrets={redactSecrets} />
        </>
      ) : null}
    </div>
  );
}

function TextareaLike({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <textarea
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder="curl 'https://api.example.com/v1/resource' -H 'Authorization: Bearer ...'"
      className="min-h-80 w-full rounded-md border border-zinc-200 bg-white px-3 py-2 font-mono text-sm leading-6 text-zinc-950 shadow-sm outline-none transition-colors placeholder:text-zinc-400 focus:border-zinc-400 focus:ring-2 focus:ring-zinc-200"
    />
  );
}

function SnippetTabs({
  activeSnippet,
  onChange,
}: {
  activeSnippet: CurlSnippetKey;
  onChange: (snippet: CurlSnippetKey) => void;
}) {
  return (
    <div className="inline-flex flex-wrap rounded-md border border-zinc-200 bg-white p-1">
      {snippetTabs.map((tab) => (
        <Button
          key={tab.id}
          type="button"
          size="sm"
          variant={activeSnippet === tab.id ? "default" : "ghost"}
          onClick={() => onChange(tab.id)}
        >
          {tab.label}
        </Button>
      ))}
    </div>
  );
}

function ExplanationPanel({
  translation,
  redactSecrets,
}: {
  translation: CurlTranslation;
  redactSecrets: boolean;
}) {
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="info">{translation.method}</Badge>
        <Badge variant={translation.body.type === "none" ? "default" : "success"}>
          {translation.body.type}
        </Badge>
        {translation.auth ? <Badge variant="warning">auth</Badge> : null}
        {translation.warnings.length ? (
          <Badge variant="warning">{translation.warnings.length} warnings</Badge>
        ) : (
          <Badge variant="success">clean parse</Badge>
        )}
      </div>

      <div className="rounded-md border border-zinc-200 bg-zinc-50 p-3">
        <p className="break-all font-mono text-sm font-medium text-zinc-950">
          {translation.url.raw}
        </p>
      </div>

      <ul className="space-y-2 text-sm leading-6 text-zinc-700">
        {translation.explanation.map((line) => (
          <li key={line} className="flex gap-2">
            <ShieldCheck className="mt-1 h-4 w-4 flex-none text-emerald-600" />
            <span>{line}</span>
          </li>
        ))}
      </ul>

      {translation.warnings.length ? (
        <WarningList warnings={translation.warnings} />
      ) : null}

      <div className="flex items-center gap-2 rounded-md border border-zinc-200 px-3 py-2 text-sm text-zinc-600">
        {redactSecrets ? (
          <EyeOff className="h-4 w-4 flex-none" />
        ) : (
          <Eye className="h-4 w-4 flex-none" />
        )}
        <span>
          {redactSecrets
            ? "Sensitive-looking values are hidden in generated output."
            : "Sensitive-looking values are visible in generated output."}
        </span>
      </div>
    </div>
  );
}

function WarningList({ warnings }: { warnings: CurlWarning[] }) {
  return (
    <div className="space-y-2">
      {warnings.map((warning) => (
        <div
          key={`${warning.id}:${warning.message}`}
          className={cn(
            "flex items-start gap-2 rounded-md border px-3 py-2 text-sm",
            warning.severity === "danger"
              ? "border-red-200 bg-red-50 text-red-800"
              : warning.severity === "warning"
                ? "border-amber-200 bg-amber-50 text-amber-800"
                : "border-cyan-200 bg-cyan-50 text-cyan-800",
          )}
        >
          {warning.severity === "danger" ? (
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-none" />
          ) : (
            <FileWarning className="mt-0.5 h-4 w-4 flex-none" />
          )}
          <span>{warning.message}</span>
        </div>
      ))}
    </div>
  );
}

function RequestDetails({
  translation,
  redactSecrets,
}: {
  translation: CurlTranslation;
  redactSecrets: boolean;
}) {
  return (
    <section className="rounded-lg border border-zinc-200 bg-white">
      <div className="flex min-h-12 items-center gap-2 border-b border-zinc-200 px-4 py-3">
        <Rows3 className="h-4 w-4 text-zinc-500" />
        <h2 className="text-sm font-semibold text-zinc-950">Request details</h2>
      </div>
      <div className="grid gap-4 p-4 xl:grid-cols-2">
        <DetailBlock title="Basics">
          <KeyValueTable
            rows={[
              ["Method", translation.method],
              ["URL", translation.url.raw],
              ["Protocol", translation.url.protocol ?? "unresolved"],
              ["Host", translation.url.hostname ?? "unresolved"],
              ["Path", translation.url.pathname ?? "unresolved"],
            ]}
          />
        </DetailBlock>

        <DetailBlock title="Options">
          <KeyValueTable
            rows={[
              ["Follow redirects", yesNo(translation.options.followRedirects)],
              ["Skip TLS verification", yesNo(translation.options.insecure)],
              ["Compressed response", yesNo(translation.options.compressed)],
              ["Include response headers", yesNo(translation.options.includeHeaders)],
              ["Timeout", translation.options.maxTime ?? "not set"],
              ["HTTP version", translation.options.httpVersion ?? "default"],
            ]}
          />
        </DetailBlock>

        <DetailBlock title="Headers">
          <HeadersTable headers={translation.headers} redactSecrets={redactSecrets} />
        </DetailBlock>

        <DetailBlock title="Query">
          <QueryTable params={translation.queryParams} redactSecrets={redactSecrets} />
        </DetailBlock>

        <DetailBlock title="Body">
          <BodyDetails translation={translation} redactSecrets={redactSecrets} />
        </DetailBlock>

        <DetailBlock title="Auth and cookies">
          <AuthDetails translation={translation} redactSecrets={redactSecrets} />
        </DetailBlock>
      </div>
    </section>
  );
}

function DetailBlock({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0 rounded-md border border-zinc-200">
      <div className="border-b border-zinc-200 bg-zinc-50 px-3 py-2">
        <h3 className="text-sm font-semibold text-zinc-800">{title}</h3>
      </div>
      <div className="p-3">{children}</div>
    </div>
  );
}

function KeyValueTable({ rows }: { rows: Array<[string, string]> }) {
  return (
    <div className="overflow-hidden rounded-md border border-zinc-200">
      <table className="w-full text-left text-sm">
        <tbody>
          {rows.map(([label, value]) => (
            <tr key={label} className="border-b border-zinc-100 last:border-0">
              <th className="w-40 bg-zinc-50 px-3 py-2 font-medium text-zinc-600">{label}</th>
              <td className="break-all px-3 py-2 font-mono text-zinc-900">{value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function HeadersTable({
  headers,
  redactSecrets,
}: {
  headers: CurlHeader[];
  redactSecrets: boolean;
}) {
  if (!headers.length) {
    return <EmptyState label="No headers found." />;
  }

  return (
    <SimpleTable
      columns={["Name", "Value"]}
      rows={headers.map((header) => [
        header.name,
        displayValue(header.value, header.sensitive, redactSecrets),
      ])}
    />
  );
}

function QueryTable({
  params,
  redactSecrets,
}: {
  params: CurlQueryParam[];
  redactSecrets: boolean;
}) {
  if (!params.length) {
    return <EmptyState label="No query parameters found." />;
  }

  return (
    <SimpleTable
      columns={["Key", "Value", "Source"]}
      rows={params.map((param) => [
        param.key,
        displayValue(param.value, param.sensitive, redactSecrets),
        param.source,
      ])}
    />
  );
}

function BodyDetails({
  translation,
  redactSecrets,
}: {
  translation: CurlTranslation;
  redactSecrets: boolean;
}) {
  if (translation.body.type === "none") {
    return <EmptyState label="No request body found." />;
  }

  if (translation.body.fields.length) {
    return (
      <div className="space-y-3">
        <Badge variant="info">{translation.body.type}</Badge>
        <BodyFieldsTable fields={translation.body.fields} redactSecrets={redactSecrets} />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <Badge variant="info">{translation.body.type}</Badge>
      <CodeOutput
        value={formatCurlBodyPreview(translation.body, redactSecrets)}
        className="max-h-72 min-h-32"
      />
    </div>
  );
}

function BodyFieldsTable({
  fields,
  redactSecrets,
}: {
  fields: CurlBodyField[];
  redactSecrets: boolean;
}) {
  return (
    <SimpleTable
      columns={["Field", "Value", "Kind"]}
      rows={fields.map((field) => [
        field.key,
        displayValue(field.value, field.sensitive, redactSecrets),
        field.kind,
      ])}
    />
  );
}

function AuthDetails({
  translation,
  redactSecrets,
}: {
  translation: CurlTranslation;
  redactSecrets: boolean;
}) {
  const rows: Array<[string, string]> = [];

  if (translation.auth) {
    rows.push(["Auth type", translation.auth.type]);
    if (translation.auth.username) {
      rows.push(["Username", translation.auth.username]);
    }
    if (translation.auth.password) {
      rows.push(["Password", displayValue(translation.auth.password, true, redactSecrets)]);
    }
    if (translation.auth.value) {
      rows.push(["Value", displayValue(translation.auth.value, true, redactSecrets)]);
    }
  }

  if (translation.cookies.length) {
    for (const cookie of translation.cookies) {
      rows.push([`Cookie ${cookie.name}`, displayValue(cookie.value, cookie.sensitive, redactSecrets)]);
    }
  }

  if (!rows.length) {
    return <EmptyState label="No auth or cookies found." />;
  }

  return <KeyValueTable rows={rows} />;
}

function SimpleTable({ columns, rows }: { columns: string[]; rows: string[][] }) {
  return (
    <div className="overflow-hidden rounded-md border border-zinc-200">
      <table className="w-full text-left text-sm">
        <thead className="bg-zinc-50 text-zinc-600">
          <tr>
            {columns.map((column) => (
              <th key={column} className="px-3 py-2 font-medium">
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={`${row.join(":")}:${rowIndex}`} className="border-t border-zinc-100">
              {row.map((value, index) => (
                <td key={`${value}:${index}`} className="break-all px-3 py-2 font-mono text-zinc-900">
                  {value}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EmptyState({ label }: { label: string }) {
  return (
    <div className="rounded-md border border-dashed border-zinc-300 bg-zinc-50 px-3 py-6 text-center text-sm text-zinc-500">
      {label}
    </div>
  );
}

function yesNo(value: boolean) {
  return value ? "yes" : "no";
}
